import type { EffectPass } from 'postprocessing'
import { EffectComposer, RenderPass } from 'postprocessing'
import type { Camera, Scene, WebGLRenderer } from 'three'
import { use } from '../../index'

class PostProcess {
  private readonly composer: EffectComposer
  private readonly renderPass: RenderPass
  private enabled = false
  private cancelRender: (() => void) | null = null
  private readonly passes: Set<EffectPass> = new Set()
  private sceneControl?: any

  constructor(scene: Scene, camera: Camera, renderer: WebGLRenderer, sceneControl?: any) {
    this.composer = new EffectComposer(renderer)
    this.renderPass = new RenderPass(scene, camera)
    this.composer.addPass(this.renderPass)
    this.sceneControl = sceneControl
  }

  public enable() {
    if (this.enabled)
      return

    this.enabled = true
    this.cancelRender = this.startRender()
  }

  public disable() {
    if (!this.enabled)
      return

    this.enabled = false
    this.cancelRender?.()
    this.cancelRender = null
  }

  public addPass(pass: EffectPass) {
    if (!pass || this.passes.has(pass))
      return

    this.passes.add(pass)
    this.composer.addPass(pass)

    return this
  }

  public removePass(pass: EffectPass) {
    if (!pass || !this.passes.has(pass))
      return

    this.passes.delete(pass)
    this.composer.removePass(pass)
  }

  private startRender = () => {
    const renderLoop = () => {
      if (!this.enabled)
        return
      this.composer.render()
    }

    if (this.sceneControl)
      return use.useframe(this.sceneControl, renderLoop)

    return () => {}
  }

  public dispose() {
    this.disable()
    this.passes.clear()
    this.composer.dispose()
  }

  public isEnabled() {
    return this.enabled
  }

  public getActivePasses() {
    return Array.from(this.passes)
  }
}

export default PostProcess
